import { KCC_QUERY_GROUPS, rayDistance } from "./physics.js";
import { RUN_SPEED, BACK_STRAFE_FACTOR, JUMP_VELOCITY, GRAVITY, AIR_ACCEL, AIR_SPEED_CAP, TERMINAL_VELOCITY, CROUCH_SPEED_FACTOR, CAPSULE_CENTER, EYE_HEIGHT, HAND_FORWARD, HAND_CLEARANCE, HAND_MIN, PUSH_DECAY, PUSH_DECAY_AIR, KINDS, Kind, FRAG_COOLDOWN, THROW_SPEED, THROW_UP, STAB_WINDUP, STAB_RECOVERY, KICK_COOLDOWN, KNIFE_THROW_SPEED, KNIFE_RETURN, STEP_DT, STAB_RANGE, STAB_CONE_COS, STAB_CONE_MIN_DIST, PLAYER_RADIUS, PLAYER_HEIGHT, } from "./tuning.js";
const f = Math.fround;
const scratchDelta = { x: 0, y: 0, z: 0 };
const scratchPos = { x: 0, y: 0, z: 0 };
export function aimDir(yaw, pitch) {
    const cp = Math.cos(pitch);
    return { x: Math.sin(yaw) * cp, y: Math.sin(pitch), z: Math.cos(yaw) * cp };
}
/** Where a throw starts: a little in front of the eyes. */
export function handPos(s, yaw, pitch, env) {
    const d = aimDir(yaw, pitch);
    const eye = s.y + (s.crouch ? EYE_HEIGHT * 0.65 : EYE_HEIGHT);
    // Never put the hand inside a wall: pull it back along the aim when something is closer than the reach.
    let reach = HAND_FORWARD;
    if (env) {
        const hit = rayDistance(env, s.x, eye, s.z, d.x, d.y, d.z, HAND_FORWARD + HAND_CLEARANCE);
        if (hit !== null)
            reach = Math.max(HAND_MIN, hit - HAND_CLEARANCE);
    }
    return { x: s.x + d.x * reach, y: eye + d.y * reach - 0.1, z: s.z + d.z * reach, d };
}
/**
 * The direction a grenade actually leaves the hand: the aim, lifted a little so a level throw
 * still lobs. The aiming arc and the two throw paths all read this, so the preview cannot
 * disagree with the throw.
 */
export function throwDir(d) {
    const dx = d.x, dy = d.y + THROW_UP, dz = d.z;
    const len = Math.hypot(dx, dy, dz) || 1;
    return { x: dx / len, y: dy / len, z: dz / len };
}
function kindInSlot(s, slot) {
    if (slot === 1)
        return Kind.Frag;
    if (slot === 2)
        return s.kind2;
    if (slot === 3)
        return s.kind3;
    return 0;
}
function takeAmmo(s, slot) {
    if (slot === 2) {
        s.ammo2--;
        if (s.ammo2 <= 0) {
            s.ammo2 = 0;
            s.kind2 = 0;
            if (s.slot === 2)
                s.slot = 1;
        }
    }
    if (slot === 3) {
        s.ammo3--;
        if (s.ammo3 <= 0) {
            s.ammo3 = 0;
            s.kind3 = 0;
            if (s.slot === 3)
                s.slot = 1;
        }
    }
}
function fire(s, cmd, e, ev) {
    const h = handPos(s, cmd.yaw, cmd.pitch, e.env);
    const d = h.d;
    const t = throwDir(d);
    const dx = t.x, dy = t.y, dz = t.z;
    if (s.slot === 4) {
        if (s.knifeOut)
            return;
        s.knifeOut = true;
        s.knifeReturn = KNIFE_RETURN;
        ev?.knifeThrow?.(h.x, h.y, h.z, d.x, d.y, d.z, KNIFE_THROW_SPEED);
        return;
    }
    const kind = kindInSlot(s, s.slot);
    if (kind === 0)
        return;
    const def = KINDS[kind];
    if (kind === Kind.Frag) {
        if (s.fragCooldown > 0)
            return;
    }
    else if ((s.slot === 2 ? s.ammo2 : s.ammo3) <= 0)
        return;
    if (def.cook) {
        // Pin out. The throw happens on release, the fuse keeps burning in the hand.
        s.cookLeft = def.fuse;
        s.cookKind = kind;
        ev?.pinPull?.(kind);
    }
    else {
        // Contact grenades leave the hand on the press.
        if (kind === Kind.Frag)
            s.fragCooldown = FRAG_COOLDOWN;
        else
            takeAmmo(s, s.slot);
        ev?.throw?.(kind, h.x, h.y, h.z, dx, dy, dz, THROW_SPEED, -1);
    }
}
function release(s, cmd, e, ev) {
    const kind = s.cookKind;
    const h = handPos(s, cmd.yaw, cmd.pitch, e.env);
    const t = throwDir(h.d);
    const dx = t.x, dy = t.y, dz = t.z;
    const fuseLeft = s.cookLeft;
    s.cookLeft = -1;
    s.cookKind = 0;
    if (kind === Kind.Frag)
        s.fragCooldown = FRAG_COOLDOWN;
    else if (s.slot === 2 && s.kind2 === kind)
        takeAmmo(s, 2);
    else if (s.slot === 3 && s.kind3 === kind)
        takeAmmo(s, 3);
    ev?.throw?.(kind, h.x, h.y, h.z, dx, dy, dz, THROW_SPEED, fuseLeft);
}
/**
 * Advance one player by one fixed step. Deterministic: reads only state, cmd and the static map.
 * yaw 0 faces +z, right is +x. The step never reads clocks, random numbers or other players.
 */
export function stepPlayer(dt, s, cmd, e) {
    const ev = e.replay ? undefined : e.events;
    // ---- counters
    if (s.fragCooldown > 0)
        s.fragCooldown--;
    if (s.kickCooldown > 0)
        s.kickCooldown--;
    if (s.stabLeft > 0)
        s.stabLeft--;
    if (s.knifeOut && s.knifeReturn > 0) {
        s.knifeReturn--;
        if (s.knifeReturn === 0)
            s.knifeOut = false;
    }
    const cooking = s.cookLeft >= 0;
    // ---- weapon slot. A change is an edge on the requested slot, so a swap made by a pickup sticks. Cooking commits you.
    if (!cooking && cmd.slot !== s.slotReq && cmd.slot >= 1 && cmd.slot <= 4) {
        s.slotReq = cmd.slot;
        if (cmd.slot !== s.slot && (cmd.slot === 1 || cmd.slot === 4 || kindInSlot(s, cmd.slot) !== 0)) {
            s.slot = cmd.slot;
            ev?.slotChange?.(cmd.slot);
        }
    }
    // ---- edges
    const fireDown = cmd.fire && !s.fireHeld;
    const fireUp = !cmd.fire && s.fireHeld;
    const kickDown = cmd.kick && !s.kickHeld;
    const stabDown = cmd.stab && !s.stabHeld;
    const interactDown = cmd.interact && !s.interactHeld;
    s.fireHeld = cmd.fire;
    s.kickHeld = cmd.kick;
    s.stabHeld = cmd.stab;
    s.interactHeld = cmd.interact;
    if (cooking) {
        if (fireUp) {
            release(s, cmd, e, ev);
        }
        else {
            s.cookLeft--;
            if (s.cookLeft < 0) {
                // Held too long. The server kills the holder, the client draws the flash and waits.
                const kind = s.cookKind;
                const h = handPos(s, cmd.yaw, cmd.pitch, e.env);
                s.cookLeft = -1;
                s.cookKind = 0;
                if (kind === Kind.Frag)
                    s.fragCooldown = FRAG_COOLDOWN;
                ev?.handDetonate?.(kind, h.x, h.y, h.z);
            }
        }
    }
    else if (fireDown && s.stabLeft === 0) {
        fire(s, cmd, e, ev);
    }
    // Read the cook from the state, not from the flag captured above: firing this same step starts one,
    // and cooking commits you, so the stab has to lose.
    if (s.cookLeft < 0 && stabDown && s.stabLeft === 0) {
        s.stabLeft = STAB_WINDUP + STAB_RECOVERY;
        ev?.stabStart?.();
    }
    if (kickDown && s.kickCooldown === 0) {
        s.kickCooldown = KICK_COOLDOWN;
        ev?.kickStart?.();
    }
    if (interactDown)
        ev?.interactStart?.();
    // ---- movement
    const fx = Math.sin(cmd.yaw), fz = Math.cos(cmd.yaw);
    const rx = fz, rz = -fx;
    let wx = fx * cmd.moveZ + rx * cmd.moveX;
    let wz = fz * cmd.moveZ + rz * cmd.moveX;
    const wl = Math.hypot(wx, wz);
    if (wl > 1) {
        wx /= wl;
        wz /= wl;
    }
    const forwardOnly = cmd.moveZ > 0 && cmd.moveX === 0;
    let speed = forwardOnly ? RUN_SPEED : RUN_SPEED * BACK_STRAFE_FACTOR;
    s.crouch = cmd.crouch;
    if (cmd.crouch && s.grounded)
        speed *= CROUCH_SPEED_FACTOR;
    if (s.grounded) {
        s.vx = wx * speed;
        s.vz = wz * speed;
        if (cmd.jump) {
            s.vy = JUMP_VELOCITY;
            s.grounded = false;
        }
    }
    else {
        // Limited air control: push toward the wish direction, never past run speed. You cannot reverse a jump.
        s.vx += wx * AIR_ACCEL * dt;
        s.vz += wz * AIR_ACCEL * dt;
        const hs = Math.hypot(s.vx, s.vz);
        if (hs > AIR_SPEED_CAP) {
            s.vx *= AIR_SPEED_CAP / hs;
            s.vz *= AIR_SPEED_CAP / hs;
        }
    }
    s.vy -= GRAVITY * dt;
    if (s.vy < -TERMINAL_VELOCITY)
        s.vy = -TERMINAL_VELOCITY;
    // Knockback rides on top of the run and fades.
    const decay = Math.exp(-(s.grounded ? PUSH_DECAY : PUSH_DECAY_AIR) * dt);
    s.px *= decay;
    s.pz *= decay;
    if (Math.abs(s.px) < 0.02)
        s.px = 0;
    if (Math.abs(s.pz) < 0.02)
        s.pz = 0;
    // The state is the truth: put the collider where the state says before asking the controller.
    scratchPos.x = s.x;
    scratchPos.y = s.y + CAPSULE_CENTER;
    scratchPos.z = s.z;
    e.pb.collider.setTranslation(scratchPos);
    scratchDelta.x = (s.vx + s.px) * dt;
    scratchDelta.y = s.vy * dt;
    scratchDelta.z = (s.vz + s.pz) * dt;
    e.env.controller.computeColliderMovement(e.pb.collider, scratchDelta, undefined, KCC_QUERY_GROUPS);
    const m = e.env.controller.computedMovement();
    const grounded = e.env.controller.computedGrounded();
    s.x = f(s.x + m.x);
    s.y = f(s.y + m.y);
    s.z = f(s.z + m.z);
    s.grounded = grounded;
    if (grounded && s.vy < 0)
        s.vy = 0;
    if (!grounded) {
        if (Math.abs(m.x) < Math.abs(scratchDelta.x) * 0.5)
            s.vx *= 0.5;
        if (Math.abs(m.z) < Math.abs(scratchDelta.z) * 0.5)
            s.vz *= 0.5;
    }
    s.vx = f(s.vx);
    s.vy = f(s.vy);
    s.vz = f(s.vz);
    s.px = f(s.px);
    s.pz = f(s.pz);
    scratchPos.x = s.x;
    scratchPos.y = s.y + CAPSULE_CENTER;
    scratchPos.z = s.z;
    e.pb.collider.setTranslation(scratchPos);
    e.pb.body.setNextKinematicTranslation(scratchPos);
}
export function placePlayer(s, x, y, z) {
    s.x = f(x);
    s.y = f(y);
    s.z = f(z);
    s.vx = 0;
    s.vy = 0;
    s.vz = 0;
    s.px = 0;
    s.pz = 0;
    s.grounded = false;
    s.crouch = false;
}
/**
 * Pay for a grenade that is still in the hand. A cook does not spend its round until release, so
 * anything that ends a cook early (death, a dropped connection) has to settle up or the round is free.
 */
export function spendCookedAmmo(s) {
    const kind = s.cookKind;
    if (kind === Kind.None)
        return;
    if (kind === Kind.Frag) {
        s.fragCooldown = FRAG_COOLDOWN;
        return;
    }
    if (s.kind2 === kind && s.ammo2 > 0)
        takeAmmo(s, 2);
    else if (s.kind3 === kind && s.ammo3 > 0)
        takeAmmo(s, 3);
}
/**
 * How far the eye is from a target's capsule surface for a stab, or null if it is out of reach or
 * behind you. STAB_RANGE measures to the body, so a target's own radius does not extend your arm.
 */
export function stabReach(ex, ey, ez, d, tx, ty, tz) {
    // Nearest point on the target's capsule axis to the eye.
    const ay = ty + PLAYER_RADIUS, by = ty + PLAYER_HEIGHT - PLAYER_RADIUS;
    const cy = Math.max(ay, Math.min(by, ey));
    const dx = tx - ex, dy = cy - ey, dz = tz - ez;
    const len = Math.hypot(dx, dy, dz);
    const surface = len - PLAYER_RADIUS;
    if (surface > STAB_RANGE)
        return null;
    if (len > STAB_CONE_MIN_DIST && (dx * d.x + dy * d.y + dz * d.z) / len < STAB_CONE_COS)
        return null;
    return Math.max(0, surface);
}
/** Fresh spawn kit: Frag and the knife, nothing else. */
export function resetKit(s) {
    s.slot = 1;
    s.kind2 = 0;
    s.ammo2 = 0;
    s.kind3 = 0;
    s.ammo3 = 0;
    s.cookLeft = -1;
    s.cookKind = 0;
    s.fragCooldown = 0;
    s.stabLeft = 0;
    s.kickCooldown = 0;
    s.knifeOut = false;
    s.knifeReturn = 0;
    s.slotReq = 1;
    // Held flags start true: a button still held from before the spawn must be released and pressed again.
    s.fireHeld = true;
    s.kickHeld = true;
    s.stabHeld = true;
    s.interactHeld = true;
}
/** Try to add a pickup. Returns true if anything was taken. Swap replaces the selected special when both slots are full. */
export function givePickup(s, kind, count, swap) {
    if (kind === Kind.Frag || kind === Kind.None)
        return false;
    const max = KINDS[kind].padCount;
    if (s.kind2 === kind) {
        if (s.ammo2 >= max)
            return false;
        s.ammo2 = Math.min(max, s.ammo2 + count);
        return true;
    }
    if (s.kind3 === kind) {
        if (s.ammo3 >= max)
            return false;
        s.ammo3 = Math.min(max, s.ammo3 + count);
        return true;
    }
    if (s.kind2 === 0) {
        s.kind2 = kind;
        s.ammo2 = Math.min(max, count);
        return true;
    }
    if (s.kind3 === 0) {
        s.kind3 = kind;
        s.ammo3 = Math.min(max, count);
        return true;
    }
    if (swap && s.cookLeft < 0) {
        if (s.slot === 3) {
            s.kind3 = kind;
            s.ammo3 = Math.min(max, count);
            return true;
        }
        s.kind2 = kind;
        s.ammo2 = Math.min(max, count);
        s.slot = 2;
        return true;
    }
    return false;
}
/** Damage at a distance from a blast. */
export function blastDamage(kind, dist) {
    const def = KINDS[kind];
    if (dist <= def.lethalR)
        return def.damage;
    if (dist >= def.falloffR)
        return 0;
    const t = (dist - def.lethalR) / (def.falloffR - def.lethalR);
    return def.damage + (def.falloffDamage - def.damage) * t;
}
export const STEP_SECONDS = STEP_DT;
//# sourceMappingURL=sim.js.map